<script setup lang="ts">
import { useArticleThemeStore } from "~/store/article/theme";
import type { ArticleSerie, Category } from "../articleTypes";
import { toast } from "vue3-toastify";

const { $privateApi } = useNuxtApp() as any;

const props = defineProps({
  theme: {
    type: Object as PropType<ArticleSerie | null>,
    required: true,
  },
  currentCategory: {
    type: Object as PropType<Category | null>,
    required: true,
  },
});

const isLoading = ref(false);

const bootstrap = (window as any).bootstrap;
let modal: any;

const action: Ref<"add" | "edit"> = ref("add");
const themeName = ref("");

const articleThemeStore = useArticleThemeStore();

const createTheme = async () => {
  try {
    if (props.currentCategory?._id) {
      isLoading.value = true;
      await $privateApi.post(`/api/bo/article/series`, {
        name: themeName.value,
        category: props.currentCategory?._id,
      });
      await articleThemeStore.getThemesByCategory(props.currentCategory?._id);
      toast.success("Thème créé avec succès");
    }
  } catch (error) {
    toast.error("Une erreur est survenue lors de la création du thème");
  } finally {
    if (modal) {
      setTimeout(() => {
        modal.hide();
        isLoading.value = false;
      }, 1000);
    }
  }
};

const updateTheme = async () => {
  try {
    if (props.currentCategory?._id) {
      isLoading.value = true;
      await $privateApi.put(`/api/bo/article/series/${props.theme?._id}`, {
        name: themeName.value,
      });
      await articleThemeStore.getThemesByCategory(props.currentCategory?._id);
      toast.success("Thème modifié avec succès");
    }
  } catch (error) {
    toast.error("Une erreur est survenue lors de la modification du thème");
  } finally {
    if (modal) {
      setTimeout(() => {
        modal.hide();
        isLoading.value = false;
      }, 1000);
    }
  }
};

const handleSubmit = async () => {
  if (themeName.value === "") {
    toast.error("Veuillez saisir un nom de thème");
    return;
  }
  if (action.value === "add") {
    await createTheme();
  } else {
    await updateTheme();
  }
};

watch(
  () => props.theme,
  () => {
    themeName.value = props.theme?.name || "";
  },
  { immediate: true }
);

onMounted(async () => {
  modal = await new bootstrap.Modal("#add_new_article_theme");
  const modalEl = document.getElementById("add_new_article_theme");
  modalEl?.addEventListener("show.bs.modal", (e: any) => {
    const button = e.relatedTarget;
    const type = button.getAttribute("data-bs-type");

    if (type === "edit") {
      action.value = type;
      if (props.theme?.name) {
        themeName.value = props.theme?.name || "";
      }
    } else if (type === "add") {
      action.value = type;
      themeName.value = "";
    }
  });
});
</script>
<template>
  <Teleport to="body">
    <div
      id="add_new_article_theme"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
            <h6 class="mb-3 text-center">
              {{
                action === "add"
                  ? "Ajouter un thème dans la catégorie " +
                    `"${currentCategory?.name}"`
                  : "Modifier le thème " + props.theme?.name
              }}
            </h6>
            <form @submit.prevent="handleSubmit">
              <div class="row gx-3">
                <div class="col-sm-12">
                  <div class="form-group">
                    <label class="form-label">Nom du thème</label>
                    <input
                      class="form-control"
                      type="text"
                      id="type"
                      v-model="themeName"
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Fermer
              </button>
              <button
                type="submit"
                class="btn btn-primary float-end"
                :disabled="isLoading"
              >
                <span v-if="isLoading">
                  <i class="bx bx-loader-circle bx-spin"></i>
                </span>
                <span v-else>
                  {{ action === "add" ? "Ajouter" : "Modifier" }}
                </span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
